/**
 * Customers API client — o cliente é o PAI das vagas (migration 0022).
 *
 * Espelho de `lib/api/opportunities.ts`: helpers finos sobre
 * `apps/api/src/routes/customers.ts`, cookie de sessão sempre incluído
 * (better-auth) e non-2xx a lançar, para o TanStack Query encaminhar pelo
 * pipeline de erro normal.
 *
 * DUAS SITUAÇÕES DE 409, DUAS CLASSES DE ERRO
 * -------------------------------------------
 * A rota devolve 409 em dois casos, e a interface reage de forma diferente a
 * cada um — exactamente como acontece com o profissional (`professional-errors.ts`):
 *
 *   • DELETE com vagas vinculadas → `CustomerLinkedError` (carrega quantas),
 *     que vira o estado BLOQUEADO do diálogo de exclusão;
 *   • POST/PATCH com CNPJ já cadastrado → `DuplicateCustomerError`, que vira
 *     mensagem SOB O CAMPO no formulário.
 *
 * Um `Error` genérico com o texto do servidor não permitiria nenhuma das duas
 * — daí as classes carregarem os dados estruturados do corpo.
 *
 * SEM SLUG (migration 0027): o cliente deixou de ter um. Era o segundo campo
 * capaz de colidir e o único que o usuário não sabia explicar — as URLs sempre
 * foram por `id`.
 */
import type { CustomerAccountManagers } from '@caliber/shared/dtos'

import { API_BASE } from '@/lib/api'
import {
  parseProblemJson,
  readProblemExtension,
  readProblemMessage,
  toExistingRecord,
  type ExistingRecord,
} from '@/lib/api/problem'

export type CustomerStatus = 'active' | 'inactive'

export interface Customer {
  id: string
  /** Nome comercial, curto — o que aparece nas listas. */
  name: string
  /** Razão social. */
  legalName: string | null
  /** Como o usuário digitou (com ou sem máscara). */
  cnpj: string | null
  /** Só dígitos — a chave de duplicidade (`uniq_customers_cnpj_normalized`). */
  cnpjNormalized: string | null
  website: string | null
  industry: string | null
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  notes: string | null
  status: CustomerStatus
  /** `null` = sem logotipo. O binário vem de `GET /:id/logo`. */
  logoKey: string | null
  logoMime: string | null
  logoSize: number | null
  /**
   * O account manager — o usuário da Genesis que cuida da conta (Story 34.2); `null`
   * = nenhum. Só o id: o nome vem da lista de usuários, que é do admin.
   */
  accountManagerUserId: string | null
  /** ISO-8601 (o `c.json` do Hono serializa as colunas Date). */
  createdAt: string
  updatedAt: string
  /**
   * Vagas vinculadas — o que bloqueia a exclusão.
   *
   * `null` nos caminhos de ESCRITA (POST/PATCH não calculam a contagem), número
   * real na lista e no detalhe — o mesmo contrato do `applicationCount` da
   * vaga. Contagem desconhecida NÃO é contagem zero: quem a renderiza mostra
   * "—", e quem decide bloqueio trata `null` como "não sei" e deixa o 409 do
   * servidor ser a verdade.
   */
  opportunityCount: number | null
}

export interface CustomersListResponse {
  items: Customer[]
  page: number
  pageSize: number
  total: number
}

export interface CreateCustomerInput {
  name: string
  legalName?: string | null
  cnpj?: string | null
  website?: string | null
  industry?: string | null
  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
  notes?: string | null
  status?: CustomerStatus
}

/**
 * O PATCH aceita também o account manager (Story 34.2) — só ele: escolhe-se no
 * cliente já criado. 422 quando não é um usuário ativo da Genesis.
 */
export type UpdateCustomerInput = Partial<CreateCustomerInput> & {
  accountManagerUserId?: string | null
}

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' }

// ------------------------------------------------------------
// Erros tipados de conflito (409)
// ------------------------------------------------------------

/**
 * Fonte única de verdade dos campos que podem colidir: o union type é inferido
 * desta tupla, não repetido à mão. Desde a 0027 é UM só — `cnpj_normalized`, o
 * único índice único que restou na tabela.
 *
 * Continua a ser uma tupla, e não uma constante solta, precisamente porque um
 * campo colidível novo se acrescenta aqui e o resto do ficheiro (o union, o
 * type-guard, a mensagem por campo no formulário) acompanha sozinho.
 */
export const CUSTOMER_CONFLICT_FIELDS = ['cnpj'] as const
export type CustomerConflictField = (typeof CUSTOMER_CONFLICT_FIELDS)[number]

function isCustomerConflictField(value: unknown): value is CustomerConflictField {
  return (
    typeof value === 'string' && (CUSTOMER_CONFLICT_FIELDS as readonly string[]).includes(value)
  )
}

/**
 * 409 de cadastro duplicado (POST/PATCH) — vira mensagem sob o campo.
 *
 * `conflicting` é o cliente que já tem o CNPJ, quando o servidor o diz (Story
 * 36.3): o cadastro rápido da vaga oferece «Usar este» se ele está ativo. A
 * corrida no índice único chega sem ele, e fica `null`.
 */
export class DuplicateCustomerError extends Error {
  readonly field: CustomerConflictField
  readonly conflicting: ExistingRecord | null

  constructor(init: {
    message: string
    field: CustomerConflictField
    conflicting?: ExistingRecord | null
  }) {
    super(init.message)
    this.name = 'DuplicateCustomerError'
    this.field = init.field
    this.conflicting = init.conflicting ?? null
  }
}

/** 409 de exclusão bloqueada (DELETE) — vira o estado bloqueado do diálogo. */
export class CustomerLinkedError extends Error {
  /**
   * As vagas vinculadas, quando o 409 é o das vagas. `null` nos outros vínculos (as
   * contas do portal, as alocações, as posições da Story 38.3): aí a frase do servidor,
   * a `message`, diz qual é o vínculo e a saída.
   */
  readonly opportunities: number | null

  constructor(init: { message: string; opportunities: number | null }) {
    super(init.message)
    this.name = 'CustomerLinkedError'
    this.opportunities = init.opportunities
  }
}

/**
 * Contagem de vagas vinculadas no corpo do problem+json.
 *
 * O helper do servidor (`apps/api/src/lib/problem.ts`) espalha os `extensions`
 * na RAIZ do corpo — mas a leitura aceita as duas formas de propósito: se um
 * dia o corpo passar a aninhar `extensions`, o diálogo de exclusão continua a
 * explicar o bloqueio em vez de cair num toast genérico.
 */
function readLinkedOpportunities(body: Record<string, unknown>): number | null {
  if (typeof body.opportunities === 'number') return body.opportunities
  const nested = body.extensions
  if (typeof nested === 'object' && nested !== null) {
    const value = (nested as Record<string, unknown>).opportunities
    if (typeof value === 'number') return value
  }
  return null
}

/**
 * Lê o problem+json de um 409 e devolve o erro tipado correspondente. NUNCA
 * lança: um corpo ilegível, `null`, ou de formato inesperado vira um `Error`
 * genérico, para o chamador ter sempre algo a relançar.
 *
 * A decisão entre as duas classes é pelo CORPO, não pelo verbo: o `title`
 * constante do contrato (`Customer has linked records`) e a contagem de vagas
 * identificam o bloqueio de exclusão; qualquer outro 409 é duplicidade.
 */
export async function parseCustomerConflict(res: Response): Promise<Error> {
  const fallback = `Falha na operação (${res.status})`
  const body = await parseProblemJson(res)
  if (!body) return new Error(fallback)

  const message =
    typeof body.detail === 'string' && body.detail.length > 0
      ? body.detail
      : typeof body.title === 'string' && body.title.length > 0
        ? body.title
        : fallback

  const linked = readLinkedOpportunities(body)
  if (linked !== null || body.title === 'Customer has linked records') {
    return new CustomerLinkedError({ message, opportunities: linked })
  }

  // `conflictField` é a resposta estruturada quando o servidor sabe qual índice
  // colidiu. Sem ela, o CNPJ é o palpite certo — é o único campo do cliente que
  // o usuário digita sabendo que pode já existir. Não inventamos um segundo.
  const field = isCustomerConflictField(body.conflictField) ? body.conflictField : 'cnpj'
  const conflicting = toExistingRecord({
    id: readProblemExtension(body, 'conflictingCustomerId'),
    name: readProblemExtension(body, 'conflictingCustomerName'),
    status: readProblemExtension(body, 'conflictingCustomerStatus'),
  })
  return new DuplicateCustomerError({ message, field, conflicting })
}

// ------------------------------------------------------------
// CRUD
// ------------------------------------------------------------

/** GET /api/customers — lista paginada, filtro de estado opcional. */
export async function listCustomers(
  params: {
    status?: CustomerStatus
    page?: number
    pageSize?: number
    signal?: AbortSignal
  } = {},
): Promise<CustomersListResponse> {
  const url = new URL(`${API_BASE}/customers`, window.location.origin)
  if (params.status) url.searchParams.set('status', params.status)
  url.searchParams.set('page', String(params.page ?? 1))
  url.searchParams.set('pageSize', String(params.pageSize ?? 50))

  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (params.signal) init.signal = params.signal

  const res = await fetch(`${url.pathname}${url.search}`, init)
  if (!res.ok) throw new Error(`Failed to list customers (${res.status})`)
  return (await res.json()) as CustomersListResponse
}

/** GET /api/customers/:id. */
export async function getCustomer(id: string, signal?: AbortSignal): Promise<Customer> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/customers/${encodeURIComponent(id)}`, init)
  if (!res.ok) throw new Error(`Failed to load customer (${res.status})`)
  return (await res.json()) as Customer
}

/** POST /api/customers. 409 = CNPJ já cadastrado. */
export async function createCustomer(body: CreateCustomerInput): Promise<Customer> {
  const res = await fetch(`${API_BASE}/customers`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (res.status === 409) throw await parseCustomerConflict(res)
  if (!res.ok) throw new Error(`Failed to create customer (${res.status})`)
  return (await res.json()) as Customer
}

/**
 * O 403 de quem não gere a conta (Story 38.2): trocar o account manager, ou excluir um
 * cliente com operações. A mensagem é a frase do servidor, para a tela a mostrar.
 */
export class CustomerForbiddenError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CustomerForbiddenError'
  }
}

/** PATCH /api/customers/:id. 409 = CNPJ já cadastrado. */
export async function updateCustomer(id: string, body: UpdateCustomerInput): Promise<Customer> {
  const res = await fetch(`${API_BASE}/customers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (res.status === 409) throw await parseCustomerConflict(res)
  // Story 38.2: o 403 de quem não gere a conta (trocar o gestor) traz a frase do servidor.
  if (res.status === 403) {
    throw new CustomerForbiddenError(
      await readProblemMessage(res, 'Você não tem permissão para trocar o account manager.'),
    )
  }
  if (!res.ok) throw new Error(`Failed to update customer (${res.status})`)
  return (await res.json()) as Customer
}

/**
 * DELETE /api/customers/:id — exclusão real (o soft-delete do cliente é o
 * estado `inactive`, uma ação à parte). 409 = há vagas vinculadas.
 */
export async function deleteCustomer(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/customers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (res.status === 409) throw await parseCustomerConflict(res)
  // Story 38.2: apagar um cliente com operações é de quem gere a conta; a frase é do servidor.
  if (res.status === 403) {
    throw new CustomerForbiddenError(
      await readProblemMessage(res, 'Você não tem permissão para excluir este cliente.'),
    )
  }
  if (!res.ok) throw new Error(`Failed to delete customer (${res.status})`)
}

// ------------------------------------------------------------
// O gestor da conta: o atual e o histórico (Story 38.2)
// ------------------------------------------------------------

/**
 * A chave do histórico do gestor da conta. Fica sob a `['customer', id]` que a ficha
 * já invalida ao gravar.
 */
export function customerAccountManagersKey(id: string) {
  return ['customer', id, 'account-managers'] as const
}

/**
 * GET /api/customers/:id/account-managers — o gestor da conta atual (da coluna, com
 * `active`) e os períodos, do mais recente ao mais antigo.
 */
export async function listCustomerAccountManagers(
  id: string,
  signal?: AbortSignal,
): Promise<CustomerAccountManagers> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/customers/${encodeURIComponent(id)}/account-managers`, init)
  if (!res.ok) throw new Error(`Failed to load account managers (${res.status})`)
  return (await res.json()) as CustomerAccountManagers
}

// ------------------------------------------------------------
// Sub-recurso: logotipo
// ------------------------------------------------------------

/**
 * URL do binário do logotipo — usada DIRETO no `src` de um `<img>`
 * (`GET /:id/logo` devolve os bytes, não um JSON com URL assinada).
 *
 * `cacheKey` (tipicamente o `updatedAt` do cliente) existe porque o endereço
 * é estável: sem ele, substituir o logotipo deixaria o browser a servir o
 * anterior da cache até a página ser recarregada com força.
 */
export function customerLogoUrl(id: string, cacheKey?: string): string {
  const base = `${API_BASE}/customers/${encodeURIComponent(id)}/logo`
  return cacheKey ? `${base}?v=${encodeURIComponent(cacheKey)}` : base
}

/**
 * POST /api/customers/:id/logo — multipart, campo `file` (png/jpeg/webp, máx.
 * 1 MB). O browser define o boundary, então NÃO se define `Content-Type` à
 * mão. Devolve o cliente atualizado (com os campos `logo_*`).
 */
export async function uploadCustomerLogo(id: string, file: File): Promise<Customer> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetch(`${API_BASE}/customers/${encodeURIComponent(id)}/logo`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    body: form,
  })
  if (!res.ok) {
    throw new Error(
      // "logo" e não "logotipo": é assim que o cartão do formulário chama o
      // campo, e um toast que usasse outro nome falaria de outra coisa.
      await readProblemMessage(res, `Falha ao enviar o logo (${res.status})`, ['detail', 'title']),
    )
  }
  return (await res.json()) as Customer
}

/** DELETE /api/customers/:id/logo — remove o logotipo. Devolve o cliente. */
export async function deleteCustomerLogo(id: string): Promise<Customer> {
  const res = await fetch(`${API_BASE}/customers/${encodeURIComponent(id)}/logo`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Falha ao remover o logo (${res.status})`, ['detail', 'title']),
    )
  }
  return (await res.json()) as Customer
}
